import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { DashboardCardCP } from 'app/screens/screen-home/components/DashboardCardCP'
import { DashboardFilterTP } from 'app/screens/screen-home/inner/DashboardFilterTP'
import { DashboardLinksUtils } from 'app/screens/screen-home/inner/DashboardLinksUtils'
import { ThemeProject } from 'config/theme/project/ThemeProject'
import { DashboardRequests } from 'submodules/nerit-aquatec-sdk/services/dashboard/DashboardRequests'
import { DashboardConformityResponseDTO } from 'submodules/nerit-aquatec-sdk/services/dashboard/dtos/responses/DashboardConformityResponseDTO'
import { RowCP } from 'submodules/nerit-framework-ui/common/components/grid/row/RowCP'
import { ColumnCP } from 'submodules/nerit-framework-ui/common/components/grid/column/ColumnCP'
import { TextCP } from 'submodules/nerit-framework-ui/common/components/text/TextCP'
import { TagCP } from 'submodules/nerit-framework-ui/common/components/tag/TagCP'
import { NotificationHelper } from 'submodules/nerit-framework-ui/common/components/notification/inner/NotificationHelper'
import { RequestHelper } from 'submodules/nerit-framework-utils/sdk-utils/request-manager/RequestHelper'
import { IApiReturn } from 'submodules/nerit-framework-utils/sdk-utils/request-manager/types/IApiReturn'

interface ICPProps {
    filter: DashboardFilterTP
}

/**
 * Situação do Monitoramento: Conformidade Ambiental e Prioridades Ambientais.
 */
export function DashboardMonitoringStatusCP(props: ICPProps): JSX.Element {

    const [conformity, setConformity] = useState<DashboardConformityResponseDTO>()
    const [isLoading, setIsLoading] = useState<boolean>(false)

    useEffect(() => { void load() }, [props.filter])

    /**
     */
    async function load(): Promise<void> {

        setIsLoading(true)
        try {
            const response = await RequestHelper.runRequest<IApiReturn<DashboardConformityResponseDTO>>(
                DashboardRequests.getConformity({
                    locationCodes: props.filter.locationCodes,
                    regulationCode: props.filter.regulationCode,
                    dateRange: props.filter.dateRange,
                }),
            )
            setConformity(response.data.data)
        } catch {
            setConformity(undefined)
            NotificationHelper.error(NotificationHelper.DEFAULT_ERROR_GET_MESSAGE)
        } finally {
            setIsLoading(false)
        }
    }

    const priorityFields = conformity?.priorityFields ?? []

    return (
        <RowCP>
            <ColumnCP size={12} xs={24} md={12}>
                <DashboardCardCP
                    title={'Conformidade Ambiental'}
                    isLoading={isLoading}
                    link={{
                        label: 'Consultar dados',
                        onClick: () => DashboardLinksUtils.goToDataAnalysis(props.filter),
                    }}
                >
                    {
                        !!conformity && conformity.totalResults === 0 &&
                        <>
                            <BigNumberSCP color={'#bfbfbf'}>-</BigNumberSCP>
                            <TextCP text={'Sem resultados com limite na norma para o filtro aplicado'} wrap={true}/>
                        </>
                    }
                    {
                        !!conformity && conformity.totalResults > 0 &&
                        <>
                            <BigNumberSCP color={ThemeProject.primary}>
                                {`${formatPercent(conformity.accordancePercent)}%`}
                            </BigNumberSCP>
                            <TextCP text={'dos resultados dentro da norma'}/>
                            <TextCP
                                marginTop={8}
                                color={'#888'}
                                size={'small'}
                                text={`${conformity.totalInAccordance.toLocaleString('pt-BR')} de ${conformity.totalResults.toLocaleString('pt-BR')} resultados com limite na norma`}
                            />
                        </>
                    }
                </DashboardCardCP>
            </ColumnCP>

            <ColumnCP size={12} xs={24} md={12}>
                <DashboardCardCP
                    title={'Prioridades Ambientais'}
                    isLoading={isLoading}
                    link={{
                        label: 'Ver no ICT',
                        onClick: () => DashboardLinksUtils.goToWaterContamination(props.filter, priorityFields.map((field) => field.code)),
                        disabled: priorityFields.length === 0,
                    }}
                >
                    {
                        !!conformity &&
                        <>
                            <BigNumberSCP color={priorityFields.length > 0 ? ThemeProject.error : ThemeProject.success}>
                                {String(priorityFields.length).padStart(2, '0')}
                            </BigNumberSCP>
                            <TextCP text={`${priorityFields.length === 1 ? 'parâmetro obteve' : 'parâmetros obtiveram'} valores acima do dobro da norma`} wrap={true}/>
                            <TagsWrapperSCP>
                                {priorityFields.map((field) => <TagCP key={field.code} content={field.name} color={'red'} small={true}/>)}
                            </TagsWrapperSCP>
                        </>
                    }
                </DashboardCardCP>
            </ColumnCP>
        </RowCP>
    )
}

function formatPercent(value: number): string {
    return value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })
}

const BigNumberSCP = styled.div<{ color: string }>`
    font-size: 40px;
    font-weight: 600;
    line-height: 1.2;
    color: ${(props) => props.color};
`

const TagsWrapperSCP = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 12px;
`
